/**
 * Admin screens
 * Users, Departments, Category Permissions, Access Requests and SSO.
 *
 * These routes exist in the live product but are gated - the demo CEO account
 * gets "Access Denied" on all five - so the layouts here are built from the
 * route names and the permission model the rest of the product implies.
 * Demo content - see src/demo/live.ts.
 */

import React, { useState } from 'react';
import { AppShell } from '../../components/shell/AppShell';
import { Card, Kpi, PageHead, Table, Tag, Note, Split, DemoBadge } from '../../components/ui';
import { Icon, ICONS } from '../../components/shell/icons';
import {
  USERS,
  DEPARTMENTS,
  CATEGORY_PERMISSIONS,
  ACCESS_REQUESTS,
  SSO_CONFIG,
} from '../../demo/live';

/* ------------------------------------------------------------- users */

export const UsersPage: React.FC = () => {
  const active = USERS.filter((u) => u.status === 'Active').length;

  return (
    <AppShell
      title="Users"
      sub="Settings"
      actions={
        <>
          <DemoBadge />
          <button className="btn btn-g">Export</button>
          <button className="btn btn-p">
            <Icon d={ICONS.plus} strokeWidth={2.4} />
            Invite user
          </button>
        </>
      }
    >
      <PageHead
        eyebrow="Settings"
        title="Who can ask, and as whom"
        lede="A user's answers are bounded by the role they hold here and the permissions their source systems already grant them. Changing a role changes what OmniMind will read on their behalf."
      />

      <div className="g g4" style={{ marginBottom: 12 }}>
        <Kpi label="Users" value={String(USERS.length)} color="var(--blue)" icon="users" />
        <Kpi label="Active" value={String(active)} color="var(--green)" icon="check" />
        <Kpi label="Invited" value="1" color="var(--amber)" icon="clock" sub="not yet signed in" />
        <Kpi label="Suspended" value="1" color="var(--red)" icon="lock" />
      </div>

      <Card title="All users" flush>
        <Table
          cols={[
            { t: 'User' },
            { t: 'Role' },
            { t: 'Department' },
            { t: 'Questions', num: true },
            { t: 'Last seen' },
            { t: 'Status' },
          ]}
          rows={USERS.map((u) => [
            <div>
              <div className="t1">{u.name}</div>
              <div className="t2">{u.email}</div>
            </div>,
            <Tag tone="b">{u.role}</Tag>,
            u.department,
            u.questions,
            <span className="dim">{u.lastSeen}</span>,
            <Tag tone={u.tone as any} dot>
              {u.status}
            </Tag>,
          ])}
        />
      </Card>

      <Note style={{ marginTop: 12 }}>
        <b>Permissions are inherited, not re-declared.</b> A role here decides which categories a
        person may ask about; what they can actually read still comes from their own access in
        Salesforce, SAP and Workday.
      </Note>
    </AppShell>
  );
};

/* ------------------------------------------------------- departments */

export const DepartmentsPage: React.FC = () => (
  <AppShell
    title="Departments"
    sub="Settings"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-p">
          <Icon d={ICONS.plus} strokeWidth={2.4} />
          Add department
        </button>
      </>
    }
  >
    <PageHead
      eyebrow="Settings"
      title="Departments"
      lede="Departments group people so that source access and category permissions are granted once, to a team, rather than person by person."
    />

    <div
      style={{
        display: 'grid',
        gap: 12,
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      }}
    >
      {DEPARTMENTS.map((d) => (
        <div className="conn" key={d.name}>
          <div className="top2">
            <span className="lg" style={{ background: 'var(--ink)' }}>
              <Icon d={ICONS.building} className="w-4 h-4" />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="n">{d.name}</div>
              <div className="m">Lead · {d.lead}</div>
            </div>
          </div>
          <div className="stats">
            <div>
              <span>Members</span>
              <b>{d.members}</b>
            </div>
            <div>
              <span>Sources</span>
              <b>{d.sources}</b>
            </div>
            <div>
              <span>Categories</span>
              <b>{d.categories}</b>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 7, marginTop: 11 }}>
            <button className="btn btn-g btn-s" style={{ flex: 1 }}>
              Manage access
            </button>
          </div>
        </div>
      ))}
    </div>
  </AppShell>
);

/* ---------------------------------------------- category permissions */

const LEVEL_TONE: Record<string, string> = {
  full: 'g',
  read: 'b',
  aggregate: 'a',
  none: 'n',
};

export const CategoryPermissionsPage: React.FC = () => (
  <AppShell title="Category Permissions" sub="Settings" actions={<DemoBadge />}>
    <PageHead
      eyebrow="Settings"
      title="What each department may ask about"
      lede="Permissions are set per data category, not per table. A question that touches a category a person cannot see is declined with a reason, never answered from a narrower slice."
    />

    <Card title="Category matrix" sub="full · read · aggregate only · none" flush>
      <Table
        cols={[
          { t: 'Category' },
          { t: 'Executive' },
          { t: 'Finance' },
          { t: 'Sales' },
          { t: 'Operations' },
          { t: 'People' },
        ]}
        rows={CATEGORY_PERMISSIONS.map((c) => [
          <b>{c.category}</b>,
          <Tag tone={LEVEL_TONE[c.exec] as any}>{c.exec}</Tag>,
          <Tag tone={LEVEL_TONE[c.finance] as any}>{c.finance}</Tag>,
          <Tag tone={LEVEL_TONE[c.sales] as any}>{c.sales}</Tag>,
          <Tag tone={LEVEL_TONE[c.ops] as any}>{c.ops}</Tag>,
          <Tag tone={LEVEL_TONE[c.people] as any}>{c.people}</Tag>,
        ])}
      />
    </Card>

    <Note style={{ marginTop: 12 }}>
      <b>"Aggregate only" is the useful middle.</b> An executive can ask what payroll costs in total
      without being able to see an individual salary — the query runs, the grain is refused.
    </Note>
  </AppShell>
);

/* -------------------------------------------------- access requests */

export const AccessRequestsPage: React.FC = () => {
  const [handled, setHandled] = useState<string[]>([]);
  const pending = ACCESS_REQUESTS.filter((r) => r.status === 'Pending' && !handled.includes(r.who));

  return (
    <AppShell
      title="Access Requests"
      sub="Settings"
      actions={
        <>
          <DemoBadge />
          {pending.length > 0 && <Tag tone="a">{pending.length} pending</Tag>}
        </>
      }
    >
      <PageHead
        eyebrow="Settings"
        title="Access requests"
        lede="When OmniMind declines a question for want of permission, the asker can request access here — with the reason attached, so the approver sees what it was for."
      />

      <div className="g g4" style={{ marginBottom: 12 }}>
        <Kpi label="Pending" value={String(pending.length)} color="var(--amber)" icon="inbox" />
        <Kpi label="Approved (30d)" value="12" color="var(--green)" icon="check" />
        <Kpi label="Declined (30d)" value="3" color="var(--red)" icon="lock" />
        <Kpi label="Median time to decide" value="4" unit=" h" color="var(--purple)" icon="clock" />
      </div>

      <Card title="Requests" flush>
        <Table
          cols={[
            { t: 'Requester' },
            { t: 'Asking for' },
            { t: 'Reason' },
            { t: 'When' },
            { t: 'Status' },
            { t: '' },
          ]}
          rows={ACCESS_REQUESTS.map((r) => {
            const isHandled = handled.includes(r.who);
            return [
              <div>
                <div className="t1">{r.who}</div>
                <div className="t2">{r.role}</div>
              </div>,
              <Tag tone="b">{r.asks}</Tag>,
              <span className="dim">{r.reason}</span>,
              <span className="dim">{r.when}</span>,
              <Tag tone={isHandled ? 'g' : (r.tone as any)}>{isHandled ? 'Approved' : r.status}</Tag>,
              r.status === 'Pending' && !isHandled ? (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="btn btn-g btn-s" onClick={() => setHandled([...handled, r.who])}>
                    Decline
                  </button>
                  <button className="btn btn-b btn-s" onClick={() => setHandled([...handled, r.who])}>
                    Approve
                  </button>
                </div>
              ) : (
                ''
              ),
            ];
          })}
        />
      </Card>
    </AppShell>
  );
};

/* ---------------------------------------------------------------- sso */

export const SsoPage: React.FC = () => (
  <AppShell
    title="Single Sign-On"
    sub="Settings"
    actions={
      <>
        <DemoBadge />
        <button className="btn btn-g">Test connection</button>
        <button className="btn btn-p">Edit configuration</button>
      </>
    }
  >
    <PageHead
      eyebrow="Settings"
      title="Single sign-on"
      lede="Identity stays where it already lives. OmniMind authenticates against your directory and inherits the groups it returns, so joiners and leavers are handled once."
    />

    <div className="g g3" style={{ marginBottom: 12 }}>
      <div className="s2">
        <Card title="Configuration" sub={SSO_CONFIG.provider}>
          {SSO_CONFIG.settings.map((s) => (
            <div className="kv" key={s.label}>
              <span className="k">{s.label}</span>
              <span className="v mono" style={{ fontSize: 12 }}>
                {s.value}
              </span>
            </div>
          ))}
        </Card>
      </div>

      <div>
        <Card title="Status">
          <div style={{ textAlign: 'center', padding: '8px 0 10px' }}>
            <div
              className="kic"
              style={{
                background: 'var(--green-s)',
                color: 'var(--green)',
                margin: '0 auto 10px',
                width: 44,
                height: 44,
              }}
            >
              <Icon d={ICONS.shield} />
            </div>
            <div style={{ fontWeight: 800, fontSize: 15 }}>{SSO_CONFIG.status}</div>
            <div className="dim" style={{ fontSize: 12 }}>
              {SSO_CONFIG.domain}
            </div>
          </div>
          <Split label="Users via SSO" value={String(SSO_CONFIG.users)} />
          <Split
            label="Enforced for all"
            value={SSO_CONFIG.enforced ? 'Yes' : 'No'}
            color={SSO_CONFIG.enforced ? 'var(--green)' : 'var(--amber)'}
          />
          <Split label="Last directory sync" value={SSO_CONFIG.lastSync} />
          <Note style={{ marginTop: 11 }}>
            With enforcement on, password sign-in is refused for everyone in this domain — including
            the demo account.
          </Note>
        </Card>
      </div>
    </div>
  </AppShell>
);
